import React, { useState, useMemo } from 'react';
import { useShift } from '../../../context/ShiftContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { SHIFT_TYPES, SHIFT_STATUS } from '../../../data/nurseAppModels';

const ShiftStatisticsPage = () => {
  const { shifts } = useShift();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [filter, setFilter] = useState('all'); // 'all', 'my', 'team'
  const [period, setPeriod] = useState('month'); // 'week', 'month', 'year', 'all'

  // Filter shifts based on filter type
  const filteredShifts = useMemo(() => {
    let filtered = [...shifts];

    // Filter by user/team
    if (filter === 'my') {
      filtered = filtered.filter(s => s.assignedTo === currentUser?.uid);
    } else if (filter === 'team') {
      // Already filtered by team in ShiftContext
    }

    // Filter by period
    const now = new Date();
    if (period === 'week') {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      filtered = filtered.filter(s => {
        const startTime = s.startTime?.toDate ? s.startTime.toDate() : new Date(s.startTime);
        return startTime >= weekAgo;
      });
    } else if (period === 'month') {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      filtered = filtered.filter(s => {
        const startTime = s.startTime?.toDate ? s.startTime.toDate() : new Date(s.startTime);
        return startTime >= monthAgo;
      });
    } else if (period === 'year') {
      const yearAgo = new Date(now);
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      filtered = filtered.filter(s => {
        const startTime = s.startTime?.toDate ? s.startTime.toDate() : new Date(s.startTime);
        return startTime >= yearAgo;
      });
    }
    // 'all' - no date filter

    return filtered;
  }, [shifts, filter, period, currentUser]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const stats = {
      total: filteredShifts.length,
      completed: 0,
      inProgress: 0,
      scheduled: 0,
      cancelled: 0,
      byType: {
        [SHIFT_TYPES.MORNING]: 0,
        [SHIFT_TYPES.AFTERNOON]: 0,
        [SHIFT_TYPES.NIGHT]: 0
      },
      totalHours: 0,
      myShifts: 0,
      myHours: 0
    };

    filteredShifts.forEach(shift => {
      // Count by status
      if (shift.status === SHIFT_STATUS.COMPLETED) stats.completed++;
      else if (shift.status === SHIFT_STATUS.IN_PROGRESS) stats.inProgress++;
      else if (shift.status === SHIFT_STATUS.SCHEDULED || shift.status === SHIFT_STATUS.CONFIRMED) stats.scheduled++;
      else if (shift.status === SHIFT_STATUS.CANCELLED) stats.cancelled++;

      // Count by type
      if (shift.shiftType) {
        stats.byType[shift.shiftType] = (stats.byType[shift.shiftType] || 0) + 1;
      }

      // Calculate hours
      if (shift.clockInTime && shift.clockOutTime) {
        const clockIn = shift.clockInTime.toDate ? shift.clockInTime.toDate() : new Date(shift.clockInTime);
        const clockOut = shift.clockOutTime.toDate ? shift.clockOutTime.toDate() : new Date(shift.clockOutTime);
        const hours = (clockOut - clockIn) / (1000 * 60 * 60);
        stats.totalHours += hours;

        if (shift.assignedTo === currentUser?.uid) {
          stats.myHours += hours;
        }
      } else if (shift.startTime && shift.endTime) {
        // Estimate hours from scheduled time
        const start = shift.startTime.toDate ? shift.startTime.toDate() : new Date(shift.startTime);
        const end = shift.endTime.toDate ? shift.endTime.toDate() : new Date(shift.endTime);
        const hours = (end - start) / (1000 * 60 * 60);
        stats.totalHours += hours;

        if (shift.assignedTo === currentUser?.uid) {
          stats.myHours += hours;
        }
      }

      // Count my shifts
      if (shift.assignedTo === currentUser?.uid) {
        stats.myShifts++;
      }
    });

    return stats;
  }, [filteredShifts, currentUser]);

  // Get shift type label
  const getShiftTypeLabel = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return 'เช้า';
      case SHIFT_TYPES.AFTERNOON: return 'บ่าย';
      case SHIFT_TYPES.NIGHT: return 'ดึก';
      default: return type;
    }
  };

  // Format hours
  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h} ชั่วโมง ${m} นาที`;
  };

  // Calculate completion rate
  const completionRate = statistics.total > 0 
    ? ((statistics.completed / statistics.total) * 100).toFixed(1)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">สถิติเวร</h1>
          <p className="text-sm text-gray-600 mt-1">สถิติและข้อมูลสรุปเกี่ยวกับเวร</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            {/* Filter by user/team */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">กรองตาม:</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">ทั้งหมด</option>
                <option value="my">เวรของฉัน</option>
                <option value="team">ทีม</option>
              </select>
            </div>

            {/* Filter by period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ช่วงเวลา:</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="week">สัปดาห์นี้</option>
                <option value="month">เดือนนี้</option>
                <option value="year">ปีนี้</option>
                <option value="all">ทั้งหมด</option>
              </select>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Shifts */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">เวรทั้งหมด</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{statistics.total}</p>
              </div>
              <div className="text-3xl">📅</div>
            </div>
          </div>

          {/* Completed Shifts */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">เสร็จสิ้น</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{statistics.completed}</p>
              </div>
              <div className="text-3xl">✅</div>
            </div>
          </div>

          {/* Total Hours */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">ชั่วโมงทำงาน</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {formatHours(statistics.totalHours)}
                </p>
              </div>
              <div className="text-3xl">⏰</div>
            </div>
          </div>

          {/* Completion Rate */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">อัตราเสร็จสมบูรณ์</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{completionRate}%</p>
              </div>
              <div className="text-3xl">📊</div>
            </div>
          </div>
        </div>

        {/* My Statistics (if filter is 'my' or 'all') */}
        {(filter === 'my' || filter === 'all') && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">สถิติของฉัน</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">เวรของฉัน</p>
                <p className="text-xl font-bold text-gray-800 mt-1">{statistics.myShifts}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">ชั่วโมงทำงาน</p>
                <p className="text-xl font-bold text-blue-600 mt-1">
                  {formatHours(statistics.myHours)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">เฉลี่ยต่อเวร</p>
                <p className="text-xl font-bold text-purple-600 mt-1">
                  {statistics.myShifts > 0 
                    ? formatHours(statistics.myHours / statistics.myShifts)
                    : '0 ชั่วโมง 0 นาที'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status Breakdown */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">แยกตามสถานะ</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-600">กำหนดแล้ว</p>
              <p className="text-2xl font-bold text-gray-700 mt-1">{statistics.scheduled}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">กำลังทำงาน</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{statistics.inProgress}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">เสร็จสิ้น</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{statistics.completed}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">ยกเลิก</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{statistics.cancelled}</p>
            </div>
          </div>
        </div>

        {/* Type Breakdown */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">แยกตามประเภทเวร</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-gray-600">เวรเช้า</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">
                {statistics.byType[SHIFT_TYPES.MORNING] || 0}
              </p>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-gray-600">เวรบ่าย</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {statistics.byType[SHIFT_TYPES.AFTERNOON] || 0}
              </p>
            </div>
            <div className="text-center p-4 bg-indigo-50 rounded-lg">
              <p className="text-sm text-gray-600">เวรดึก</p>
              <p className="text-2xl font-bold text-indigo-600 mt-1">
                {statistics.byType[SHIFT_TYPES.NIGHT] || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {statistics.total === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">📊</div>
            <p className="text-gray-600">ไม่มีข้อมูลเวรในช่วงเวลาที่เลือก</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftStatisticsPage;



